import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button, Card, Spinner } from 'react-bootstrap';
import { FaPrint, FaArrowLeft } from 'react-icons/fa';
import './QueueTicket.css';

const QueueTicket = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [queueData, setQueueData] = useState(null);
  const [clinic, setClinic] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchQueueData();
    fetchClinicData();
  }, [id]);

  const fetchQueueData = async () => {
    try {
      const res = await axios.get(`/api/queue/${id}`);
      console.log('QueueTicket - Raw data:', res.data);
      console.log('QueueTicket - queueNumber type:', typeof res.data.queueNumber);
      console.log('QueueTicket - queueNumber value:', res.data.queueNumber);
      console.log('QueueTicket - queueNumber JSON:', JSON.stringify(res.data.queueNumber));
      
      // Handle both single object and array response
      let queueData = res.data;
      if (Array.isArray(res.data) && res.data.length > 0) {
        queueData = res.data[0];
      }
      
      // Debug queue number outside JSX
      console.log('QueueTicket Debug - queueNumber:', queueData.queueNumber, 'Type:', typeof queueData.queueNumber);
      console.log('QueueTicket Debug - String conversion:', String(queueData.queueNumber || '').trim());
      
      setQueueData(queueData);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching queue data:', err);
      setLoading(false);
    }
  };

  const fetchClinicData = async () => {
    try {
      const res = await axios.get('/api/clinic.php');
      setClinic(res.data);
    } catch (err) {
      console.error('Error fetching clinic data:', err);
    }
  };

  const handlePrint = () => {
    // Hide all non-printable elements
    const noPrintElements = document.querySelectorAll('.no-print');
    noPrintElements.forEach(el => el.style.display = 'none');
    
    // Trigger print
    setTimeout(() => {
      window.print();
      
      // Restore elements after print dialog
      setTimeout(() => {
        noPrintElements.forEach(el => el.style.display = '');
      }, 100);
    }, 100);
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!queueData) {
    return (
      <div className="text-center my-5">
        <h3>Queue ticket not found</h3>
        <Button onClick={() => navigate('/queue')}>Back to Queue</Button>
      </div>
    );
  }

  return (
    <div className="queue-ticket-container">
      <div className="no-print">
        <Button variant="secondary" onClick={() => navigate('/queue')} className="me-2">
          <FaArrowLeft className="me-2" /> Back
        </Button>
        <Button variant="primary" onClick={handlePrint}>
          <FaPrint className="me-2" /> Print Ticket
        </Button>
      </div>

      <div className="ticket-print-area">
        <Card className="queue-ticket">
          <Card.Body>
            <div className="ticket-header">
              <h2>
                {clinic?.logo ? (
                  <img src={clinic.logo} alt="Logo" className="clinic-logo" />
                ) : (
                  '🏥'
                )}
                {' '}Queue Token
              </h2>
              <p className="clinic-name">{clinic?.name || 'Doctero Clinic'}</p>
              {clinic?.contactNumber && (
                <p className="clinic-contact">{clinic.contactNumber}</p>
              )}
            </div>

            <div className="ticket-token-number">
              <h1 className="token-display">#{queueData.queueNumber}</h1>
              {queueData.isEmergency === 1 && (
                <div className="emergency-label">⚠️ EMERGENCY</div>
              )}
            </div>

            <div className="ticket-details">
              <div className="detail-row">
                <span className="label">Patient:</span>
                <span className="value">{queueData.Patient?.name}</span>
              </div>
              <div className="detail-row">
                <span className="label">Doctor:</span>
                <span className="value">Dr. {queueData.Doctor?.name}</span>
              </div>
              <div className="detail-row">
                <span className="label">Date:</span>
                <span className="value">{new Date(queueData.registeredAt).toLocaleDateString()}</span>
              </div>
              <div className="detail-row">
                <span className="label">Time:</span>
                <span className="value">{(() => {
                  // Parse the datetime and add Pakistan timezone offset (UTC+5)
                  const dateStr = queueData.registeredAt.replace(' ', 'T');
                  const date = new Date(dateStr);
                  date.setHours(date.getHours() + 5);
                  
                  const hours = date.getHours();
                  const minutes = date.getMinutes().toString().padStart(2, '0');
                  const ampm = hours >= 12 ? 'PM' : 'AM';
                  const formattedHour = hours % 12 || 12;
                  return `${formattedHour}:${minutes} ${ampm}`;
                })()}</span>
              </div>
              {queueData.Appointment?.appointmentTime && (
                <div className="detail-row">
                  <span className="label">Appointment:</span>
                  <span className="value">{(() => {
                    const timeStr = queueData.Appointment.appointmentTime;
                    if (timeStr && timeStr.match(/^\d{1,2}:\d{2}$/)) {
                      const [hours, minutes] = timeStr.split(':');
                      const hour = parseInt(hours, 10);
                      const ampm = hour >= 12 ? 'PM' : 'AM';
                      const formattedHour = hour % 12 || 12;
                      return `${formattedHour}:${minutes} ${ampm}`;
                    }
                    return timeStr;
                  })()}</span>
                </div>
              )}
              {queueData.Billing && (
                <div className="detail-row">
                  <span className="label">Bill No:</span>
                  <span className="value">{queueData.Billing.billNumber}</span>
                </div>
              )}
            </div>

            <div className="ticket-instructions">
              <p>✓ Please wait in the waiting area</p>
              <p>✓ Watch the display screen for your token</p>
              <p>✓ Keep this ticket with you</p>
            </div>

            <div className="ticket-footer">
              <p>Thank you for visiting!</p>
              <p style={{fontSize: '0.8rem', color: '#666', marginTop: '10px'}}>Developer: Abdullah Arif | +92-3137323866</p>
            </div>
          </Card.Body>
        </Card>
      </div>
    </div>
  );
};

export default QueueTicket;
